import { createContext, FC, MouseEvent, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { equalInLC, toggleInArray } from "@datassential/platform-ui-lib";
import { useFilters, useMapFiltersToApi } from "@vms/SelectFilters/SelectFiltersContext";
import { useRestaurantMenu } from "@vms/RestaurantMenu";
import useTopTrendsData, { TopTrendsRequest, TopTrendsDataItem } from "@apiHooks/useTopTrendsData";
import { components } from '@api/api';
import { currentCountrySelector, currentCountryWithPermissionsSelector } from "@selectors/countries.selector";
import { TopTrendsExportRequest } from "@apiHooks/useTopTrendsExport";
import useQueryState from "@hooks/useQueryState";
import { CategoryItem } from "./TopTrendsControls";
import { FlavorDataItem } from "./TopTrendsTableConfig";

type FilterWord = components['schemas']['MenuTrends.Application.Contracts.Models.FilterWord'];
type FilterInput = components['schemas']['Application.Common.Models.MainFilterInput.Filter'];

const YEARS = [1, 2, 3, 4, 5, 10];
const TAKE = 20000;
const DEFAULT_GROWTH_PERIOD = 4;
const DEFAULT_INCLUDE_LOW_PENETRATION = false;
const DEFAULT_INCLUDE_LESS_TEN_MENUS = true;

const TOP_TRENDS_CATEGORIES = [
    { id: 1, name: 'Sauce/Flavor' },
    { id: 2, name: 'Spice' },
    { id: 3, name: 'Protein' },
    { id: 6, name: 'Veggie' },
    { id: 7, name: 'Fruit' },
    { id: 5, name: 'Cheese' },
    { id: 4, name: 'Bread' },
    { id: 9, name: 'Noodle' },
    { id: 8, name: 'Nut' },
    { id: 10, name: 'Appetizer' },
    { id: 11, name: 'Entree' },
    { id: 12, name: 'Side' },
    { id: 13, name: 'Dessert' },
    { id: 14, name: 'Non Alc Bev' },
    { id: 15, name: 'Cocktail' },
    { id: 16, name: 'Alcohol Type' },
    { id: 18, name: 'Prep Method' },
    { id: 19, name: 'Healthy' },
    { id: 20, name: 'Descriptor' },
    { id: 21, name: 'Brand' },
    { id: 22, name: 'Location' },
];

const BASE_CATEGORIES: CategoryItem[] = TOP_TRENDS_CATEGORIES.map(cat => ({
    id: cat.id,
    name: cat.name,
    isSelected: false,
}));

const QUERY_KEY_NEW_ITEMS = 'new-items';
const QUERY_KEY_PENETRATION = 'penetration';
const QUERY_KEY_CATEGORY = 'category';

const PENETRATION_ABOVE_1_PERCENT = 'above-1-percent';
const PENETRATION_ABOVE_10_MENUS = 'above-10-menus';
const PENETRATION_ALL = 'all';

function parsePenetrationParam(value: string): { includeLowPenetrationItems: boolean; includeLessTenMenusCount: boolean } {
    switch (value) {
        case PENETRATION_ABOVE_1_PERCENT:
            return { includeLowPenetrationItems: false, includeLessTenMenusCount: true };
        case PENETRATION_ABOVE_10_MENUS:
            return { includeLowPenetrationItems: true, includeLessTenMenusCount: false };
        case PENETRATION_ALL:
            return { includeLowPenetrationItems: true, includeLessTenMenusCount: true };
        default:
            return { includeLowPenetrationItems: false, includeLessTenMenusCount: true };
    }
}

function buildPenetrationParam(includeLow: boolean, includeMenus: boolean): string {
    if (includeLow && includeMenus) return PENETRATION_ALL;
    if (includeLow && !includeMenus) return PENETRATION_ABOVE_10_MENUS;
    return PENETRATION_ABOVE_1_PERCENT;
}

const DEFAULT_PENETRATION = buildPenetrationParam(
    DEFAULT_INCLUDE_LOW_PENETRATION,
    DEFAULT_INCLUDE_LESS_TEN_MENUS
);

type FilterState = {
    searchQuery: string;
    growthPeriod: number;
    drillWords: FilterWord[];
};

const initialFilterState: FilterState = {
    searchQuery: '',
    growthPeriod: DEFAULT_GROWTH_PERIOD,
    drillWords: [],
};

function buildApiRequest(
    showOnlyNewItems: boolean,
    includeLowPenetrationItems: boolean,
    includeLessTenMenusCount: boolean,
    categories: CategoryItem[],
    growthPeriod: number,
    drillWords: FilterWord[],
    skip: number = 0,
    backendFilters?: FilterInput
): TopTrendsRequest {
    const selectedCategories = categories.filter(cat => cat.isSelected);
    const topTrendCategories = selectedCategories.length > 0
        ? selectedCategories.map(cat => ({ id: cat.id, name: cat.name }))
        : categories.map(cat => ({ id: cat.id, name: cat.name }));

    return {
        mainFilter: {
            filters: backendFilters,
            topTrendCategories,
            includeLessOnePercentPenetration: includeLowPenetrationItems,
            includeLessTenMenusCount: includeLessTenMenusCount,
            isNew: showOnlyNewItems,
            yearsGrowth: growthPeriod,
            ...(drillWords.length > 0 ? { drillWords } : {}),
        },
        tableParameters: {
            sortColumn: 0,
            sortDir: 1,
            skip,
            take: TAKE,
        },
    };
}

interface TopTrendsContextType {
    isLoading: boolean;
    tableData: TopTrendsDataItem[];
    flavorData: FlavorDataItem[];
    hasFlavorData: boolean;
    hasFlavorPermission: boolean;
    totalRecords: number;
    exportRequest: TopTrendsExportRequest;
    backendFilters?: FilterInput;

    searchQuery: string;
    showOnlyNewItems: boolean;
    hasNewItemsPermission: boolean;
    includeLowPenetrationItems: boolean;
    includeLessTenMenusCount: boolean;
    categories: CategoryItem[];
    growthPeriod: number;
    drillWords: FilterWord[];
    years: number[];

    isSelectable: boolean;
    selectedWords: string[];
    hasTags: boolean;

    onSearchChange: (value: string) => void;
    onNewItemsToggle: () => void;
    onIncludeOptionsChange: (options: { includeLowPenetrationItems: boolean; includeLessTenMenusCount: boolean }) => void;
    onCategoriesApply: (categoryIds: number[]) => void;
    onCategoriesClear: () => void;
    onGrowthPeriodChange: (period: number) => void;
    onDrillClick: (word: FilterWord) => void;
    onDeleteDrillWord: (wordId: number) => void;
    onReset: () => void;

    onSelectableToggle: () => void;
    onWordSelect: (wordName: string) => void;
    onRemoveSelectedWord: (word: string) => void;
    onClearSelections: () => void;

    onSettingsClick: (e: MouseEvent<HTMLDivElement>, word: FilterWord) => void;
    keywordSettingsState: {
        keyword: FilterWord | null;
        isSettingsShown: boolean;
        settingsX: number;
        settingsY: number;
    };
    onCloseKeywordSettings: () => void;

    onMenuExamplesClick: (word: FilterWord) => void;
    menuExamplesWordData: FilterWord | null;
    isMenuExamplesPopupOpened: boolean;
    onCloseMenuExamplesPopup: () => void;

    onCopyContentChange: (content: string) => void;
    copyTableContent: string;
}

const TopTrendsContext = createContext<TopTrendsContextType | null>(null);

export const useTopTrends = (): TopTrendsContextType => {
    const context = useContext(TopTrendsContext);
    if (!context) {
        throw new Error('useTopTrends must be used within TopTrendsProvider');
    }
    return context;
};

export const TopTrendsProvider: FC<PropsWithChildren<{}>> = ({ children }) => {
    const currentCountry = useSelector(currentCountrySelector);
    const countryCode = useSelector(currentCountrySelector);
    const permissions = useSelector(currentCountryWithPermissionsSelector) as { hasNewItemsOnly?: boolean } | undefined;
    const hasNewItemsPermission = !!permissions?.hasNewItemsOnly;

    const { filters } = useFilters();
    const { activeRestaurantListId, restaurantLists, isLoadingRestaurantLists, rawFilterData } = useRestaurantMenu();
    const mapFiltersToApi = useMapFiltersToApi();
    const hasTags = (rawFilterData?.tagCategories?.length ?? 0) > 0;

    const activeRestaurantList = useMemo(
        () => restaurantLists.find(list => list.id === activeRestaurantListId),
        [restaurantLists, activeRestaurantListId]
    );

    const backendFilters = useMemo((): FilterInput | undefined => {
        if (!activeRestaurantListId) return undefined;
        return mapFiltersToApi(
            filters,
            countryCode,
            activeRestaurantListId,
            activeRestaurantList?.name || undefined
        );
    }, [filters, countryCode, activeRestaurantListId, activeRestaurantList?.name, mapFiltersToApi]);

    const {
        data: topTrendsData,
        isLoading: isTopTrendsDataLoading,
        doFetch: refetchTopTrendsData,
    } = useTopTrendsData();

    const [newItemsParam, setNewItemsParam] = useQueryState<string>(QUERY_KEY_NEW_ITEMS, '');
    const [penetrationParam, setPenetrationParam] = useQueryState<string>(QUERY_KEY_PENETRATION, DEFAULT_PENETRATION);
    const [categoryParam, setCategoryParam] = useQueryState<string>(QUERY_KEY_CATEGORY, '');

    const showOnlyNewItems = newItemsParam === 'true';
    const { includeLowPenetrationItems, includeLessTenMenusCount } = useMemo(
        () => parsePenetrationParam(penetrationParam),
        [penetrationParam]
    );
    const categories = useMemo(
        () => {
            if (!categoryParam) return BASE_CATEGORIES;
            const selectedNames = categoryParam.split(',').map(name => decodeURIComponent(name.trim().toLowerCase()));
            return BASE_CATEGORIES.map(cat => ({
                ...cat,
                isSelected: selectedNames.includes(cat.name.toLowerCase()),
            }));
        },
        [categoryParam]
    );

    const [topTrendsFilters, setTopTrendsFilters] = useState<FilterState>(initialFilterState);

    const [accumulatedData, setAccumulatedData] = useState<TopTrendsDataItem[]>([]);
    const [accumulatedFlavorData, setAccumulatedFlavorData] = useState<NonNullable<NonNullable<typeof topTrendsData>['flavorData']>['data']>([]);
    const lastProcessedDataRef = useRef<typeof topTrendsData>(null);

    const [isInitialized, setIsInitialized] = useState(false);

    const [selectedWords, setSelectedWords] = useState<string[]>([]);
    const [isSelectable, setIsSelectable] = useState(false);

    const [keywordSettingsState, setKeywordSettingsState] = useState<{
        keyword: FilterWord | null;
        isSettingsShown: boolean;
        settingsX: number;
        settingsY: number;
    }>({
        keyword: null,
        isSettingsShown: false,
        settingsX: 0,
        settingsY: 0,
    });

    const [isMenuExamplesPopupOpened, setIsMenuExamplesPopupOpened] = useState(false);
    const [menuExamplesWordData, setMenuExamplesWordData] = useState<FilterWord | null>(null);

    const [copyTableContent, setCopyTableContent] = useState<string>('');

    const years = useMemo(() => YEARS.filter(year => year < 11), []);

    const refetchDataRef = useRef<(overrides?: {
        showOnlyNewItems?: boolean;
        includeLowPenetrationItems?: boolean;
        includeLessTenMenusCount?: boolean;
        categories?: CategoryItem[];
        growthPeriod?: number;
        drillWords?: FilterWord[];
    }) => void>(() => {});

    const refetchData = useCallback((
        overrides: {
            showOnlyNewItems?: boolean;
            includeLowPenetrationItems?: boolean;
            includeLessTenMenusCount?: boolean;
            categories?: CategoryItem[];
            growthPeriod?: number;
            drillWords?: FilterWord[];
        } = {}
    ) => {
        setIsInitialized(false);
        setAccumulatedData([]);
        setAccumulatedFlavorData([]);
        lastProcessedDataRef.current = null;

        const request = buildApiRequest(
            overrides.showOnlyNewItems ?? showOnlyNewItems,
            overrides.includeLowPenetrationItems ?? includeLowPenetrationItems,
            overrides.includeLessTenMenusCount ?? includeLessTenMenusCount,
            overrides.categories ?? categories,
            overrides.growthPeriod ?? topTrendsFilters.growthPeriod,
            overrides.drillWords ?? topTrendsFilters.drillWords,
            0,
            backendFilters
        );
        refetchTopTrendsData(request);
    }, [
        refetchTopTrendsData,
        topTrendsFilters.growthPeriod,
        topTrendsFilters.drillWords,
        showOnlyNewItems,
        includeLowPenetrationItems,
        includeLessTenMenusCount,
        categories,
        backendFilters,
    ]);

    refetchDataRef.current = refetchData;

    const updateFiltersAndRefetch = useCallback((updates: Partial<FilterState>) => {
        const newGrowthPeriod = updates.growthPeriod ?? topTrendsFilters.growthPeriod;
        const newDrillWords = updates.drillWords ?? topTrendsFilters.drillWords;

        setTopTrendsFilters(prev => ({ ...prev, ...updates }));
        refetchData({
            growthPeriod: newGrowthPeriod,
            drillWords: newDrillWords,
        });
    }, [refetchData, topTrendsFilters.growthPeriod, topTrendsFilters.drillWords]);

    useEffect(() => {
        if (!backendFilters || isLoadingRestaurantLists) return;
        refetchDataRef.current();
    }, [backendFilters, isLoadingRestaurantLists]);

    const setCategoryParamRef = useRef(setCategoryParam);
    setCategoryParamRef.current = setCategoryParam;

    useEffect(() => {
        if (!activeRestaurantListId || isLoadingRestaurantLists) return;
        setCategoryParamRef.current('');
        setTopTrendsFilters(initialFilterState);
    }, [activeRestaurantListId, isLoadingRestaurantLists]);

    useEffect(() => {
        if (!topTrendsData?.data || topTrendsData === lastProcessedDataRef.current) {
            return;
        }
        lastProcessedDataRef.current = topTrendsData;

        setAccumulatedData(topTrendsData.data);
        setAccumulatedFlavorData(topTrendsData.flavorData?.data ?? []);
        setIsInitialized(true);
    }, [topTrendsData]);

    const handleSearchChange = useCallback((value: string) => {
        setTopTrendsFilters(prev => ({ ...prev, searchQuery: value }));
    }, []);

    const handleNewItemsToggle = useCallback(() => {
        const newValue = !showOnlyNewItems;
        setNewItemsParam(newValue ? 'true' : '');
        refetchData({ showOnlyNewItems: newValue });
    }, [showOnlyNewItems, setNewItemsParam, refetchData]);

    const handleIncludeOptionsChange = useCallback((options: {
        includeLowPenetrationItems: boolean;
        includeLessTenMenusCount: boolean;
    }) => {
        const newParam = buildPenetrationParam(options.includeLowPenetrationItems, options.includeLessTenMenusCount);
        setPenetrationParam(newParam);
        refetchData(options);
    }, [setPenetrationParam, refetchData]);

    const handleCategoriesApply = useCallback((categoryIds: number[]) => {
        const newCategories = BASE_CATEGORIES.map(cat => ({
            ...cat,
            isSelected: categoryIds.includes(cat.id),
        }));
        const selectedNames = BASE_CATEGORIES
            .filter(cat => categoryIds.includes(cat.id))
            .map(cat => cat.name.toLowerCase());
        setCategoryParam(selectedNames.join(','));
        refetchData({ categories: newCategories });
    }, [setCategoryParam, refetchData]);

    const handleCategoriesClear = useCallback(() => {
        const newCategories = BASE_CATEGORIES.map(cat => ({
            ...cat,
            isSelected: false,
        }));
        setCategoryParam('');
        refetchData({ categories: newCategories });
    }, [setCategoryParam, refetchData]);

    const handleGrowthPeriodChange = useCallback((period: number) => {
        updateFiltersAndRefetch({ growthPeriod: period });
    }, [updateFiltersAndRefetch]);

    const handleDrillClick = useCallback((word: FilterWord) => {
        setTopTrendsFilters(prev => ({ ...prev, searchQuery: '' }));
        updateFiltersAndRefetch({
            drillWords: [...topTrendsFilters.drillWords, word],
        });
    }, [updateFiltersAndRefetch, topTrendsFilters.drillWords]);

    const handleDeleteDrillWord = useCallback((wordId: number) => {
        const newDrillWords = topTrendsFilters.drillWords.filter(w => w.id !== wordId);
        updateFiltersAndRefetch({ drillWords: newDrillWords });
    }, [updateFiltersAndRefetch, topTrendsFilters.drillWords]);

    const handleReset = useCallback(() => {
        setIsInitialized(false);
        setNewItemsParam('');
        setPenetrationParam(DEFAULT_PENETRATION);
        setCategoryParam('');
        setTopTrendsFilters(initialFilterState);
        setAccumulatedData([]);
        setAccumulatedFlavorData([]);
        lastProcessedDataRef.current = null;
        refetchData({
            showOnlyNewItems: false,
            includeLowPenetrationItems: false,
            includeLessTenMenusCount: true,
            categories: BASE_CATEGORIES,
            growthPeriod: initialFilterState.growthPeriod,
            drillWords: [],
        });
    }, [refetchData, setNewItemsParam, setPenetrationParam, setCategoryParam]);

    const handleSelectableToggle = useCallback(() => {
        setIsSelectable(prev => !prev);
    }, []);

    const handleWordSelect = useCallback((wordName: string) => {
        setSelectedWords(prev => toggleInArray(prev, wordName));
    }, []);

    const handleRemoveSelectedWord = useCallback((word: string) => {
        setSelectedWords(prev => prev.filter(w => w !== word));
    }, []);

    const handleClearSelections = useCallback(() => {
        setSelectedWords([]);
    }, []);

    const handleSettingsClick = useCallback((e: MouseEvent<HTMLDivElement>, word: FilterWord) => {
        const boundingRect = (e.target as HTMLElement).getBoundingClientRect();
        setKeywordSettingsState({
            isSettingsShown: true,
            settingsX: boundingRect.left + 30,
            settingsY: boundingRect.top + boundingRect.height / 2,
            keyword: word,
        });
    }, []);

    const handleCloseKeywordSettings = useCallback(() => {
        setKeywordSettingsState(prev => ({
            ...prev,
            isSettingsShown: false,
        }));
    }, []);

    const handleMenuExamplesClick = useCallback((word: FilterWord) => {
        setIsMenuExamplesPopupOpened(true);
        setMenuExamplesWordData(word);
    }, []);

    const handleCloseMenuExamplesPopup = useCallback(() => {
        setIsMenuExamplesPopupOpened(false);
    }, []);

    const handleCopyContentChange = useCallback((content: string) => {
        setCopyTableContent(content);
    }, []);

    const searchIndex = useMemo(() =>
        accumulatedData.map(item => item.word?.name?.toLowerCase() ?? ''),
        [accumulatedData]
    );

    const tableData = useMemo(() => {
        const query = topTrendsFilters.searchQuery.trim();
        if (!query) {
            return accumulatedData;
        }
        const searchLower = query.toLowerCase();
        return accumulatedData.filter((_, index) =>
            searchIndex[index].includes(searchLower)
        );
    }, [accumulatedData, searchIndex, topTrendsFilters.searchQuery]);

    const flavorDataMap = useMemo((): Map<number, FlavorDataItem> => {
        if (!accumulatedFlavorData || accumulatedData.length === 0) {
            return new Map();
        }

        const map = new Map<number, FlavorDataItem>();
        accumulatedData.forEach((item, index) => {
            const flavorItem = accumulatedFlavorData[index];
            if (item.word?.id != null) {
                map.set(item.word.id, {
                    wordId: item.word.id,
                    knowIt: flavorItem?.knowIt != null ? `${Math.round(flavorItem.knowIt)}%` : '-',
                    haveTriedIt: flavorItem?.haveTriedIt != null ? `${Math.round(flavorItem.haveTriedIt)}%` : '-',
                    loveOrLikeIt: flavorItem?.loveOrLikeIt != null ? `${Math.round(flavorItem.loveOrLikeIt)}%` : '-',
                });
            }
        });
        return map;
    }, [accumulatedData, accumulatedFlavorData]);

    const flavorData = useMemo(
        () => Array.from(flavorDataMap.values()),
        [flavorDataMap]
    );

    const hasFlavorData = useMemo(
        () => equalInLC(currentCountry, 'USA'),
        [currentCountry]
    );

    const hasFlavorPermission = useMemo(
        () => (topTrendsData?.flavorData?.accessLevel ?? 0) > 0,
        [topTrendsData?.flavorData?.accessLevel]
    );

    const totalRecords = topTrendsData?.totalRecords ?? 0;

    const exportRequest = useMemo((): TopTrendsExportRequest => {
        const currentRequest = buildApiRequest(
            showOnlyNewItems,
            includeLowPenetrationItems,
            includeLessTenMenusCount,
            categories,
            topTrendsFilters.growthPeriod,
            topTrendsFilters.drillWords,
            0,
            backendFilters
        );
        return {
            fullFilter: currentRequest.mainFilter?.filters,
            customerFilter: {
                mainFilter: currentRequest.mainFilter,
                tableParameters: currentRequest.tableParameters,
            },
        };
    }, [topTrendsFilters.growthPeriod, topTrendsFilters.drillWords, showOnlyNewItems, includeLowPenetrationItems, includeLessTenMenusCount, categories, backendFilters]);

    const value = useMemo<TopTrendsContextType>(() => ({
        isLoading: isTopTrendsDataLoading || !isInitialized,
        tableData,
        flavorData,
        hasFlavorData,
        hasFlavorPermission,
        totalRecords,
        exportRequest,
        backendFilters,

        searchQuery: topTrendsFilters.searchQuery,
        showOnlyNewItems,
        hasNewItemsPermission,
        includeLowPenetrationItems,
        includeLessTenMenusCount,
        categories,
        growthPeriod: topTrendsFilters.growthPeriod,
        drillWords: topTrendsFilters.drillWords,
        years,

        isSelectable,
        selectedWords,
        hasTags,

        onSearchChange: handleSearchChange,
        onNewItemsToggle: handleNewItemsToggle,
        onIncludeOptionsChange: handleIncludeOptionsChange,
        onCategoriesApply: handleCategoriesApply,
        onCategoriesClear: handleCategoriesClear,
        onGrowthPeriodChange: handleGrowthPeriodChange,
        onDrillClick: handleDrillClick,
        onDeleteDrillWord: handleDeleteDrillWord,
        onReset: handleReset,

        onSelectableToggle: handleSelectableToggle,
        onWordSelect: handleWordSelect,
        onRemoveSelectedWord: handleRemoveSelectedWord,
        onClearSelections: handleClearSelections,

        onSettingsClick: handleSettingsClick,
        keywordSettingsState,
        onCloseKeywordSettings: handleCloseKeywordSettings,

        onMenuExamplesClick: handleMenuExamplesClick,
        menuExamplesWordData,
        isMenuExamplesPopupOpened,
        onCloseMenuExamplesPopup: handleCloseMenuExamplesPopup,

        onCopyContentChange: handleCopyContentChange,
        copyTableContent,
    }), [
        isTopTrendsDataLoading,
        isInitialized,
        tableData,
        flavorData,
        hasFlavorData,
        hasFlavorPermission,
        totalRecords,
        exportRequest,
        backendFilters,
        topTrendsFilters.searchQuery,
        topTrendsFilters.growthPeriod,
        topTrendsFilters.drillWords,
        showOnlyNewItems,
        hasNewItemsPermission,
        includeLowPenetrationItems,
        includeLessTenMenusCount,
        categories,
        years,
        isSelectable,
        selectedWords,
        hasTags,
        handleSearchChange,
        handleNewItemsToggle,
        handleIncludeOptionsChange,
        handleCategoriesApply,
        handleCategoriesClear,
        handleGrowthPeriodChange,
        handleDrillClick,
        handleDeleteDrillWord,
        handleReset,
        handleSelectableToggle,
        handleWordSelect,
        handleRemoveSelectedWord,
        handleClearSelections,
        handleSettingsClick,
        keywordSettingsState,
        handleCloseKeywordSettings,
        handleMenuExamplesClick,
        menuExamplesWordData,
        isMenuExamplesPopupOpened,
        handleCloseMenuExamplesPopup,
        handleCopyContentChange,
        copyTableContent,
    ]);

    return (
        <TopTrendsContext.Provider value={value}>
            {children}
        </TopTrendsContext.Provider>
    );
};

export default TopTrendsContext;
